import * as Yup from 'yup';
import { DeliveryStatus, Locale } from '@synergy/models';
import { fields } from '@synergy/utils';
import { yupUniqueTestFunction } from '@synergy/utils';

import { ProductDetailOverviewFormValues } from './models/product-detail-overview-form-values';

const fieldNames = fields<ProductDetailOverviewFormValues>();

export const productDetailOverviewValidationSchema = (selectedLanguage: Locale) =>
    Yup.object().shape(
        {
            [fieldNames.name]: Yup.object().test('needsTranslation', '', name => {
                const values = Object.values(name ?? {});
                return values.some(x => !!x)
                    ? true
                    : new Yup.ValidationError('app.error.required', undefined, `name.${selectedLanguage}`);
            }),
            [fieldNames.number]: Yup.string().required('app.error.required'),
            [fieldNames.orderUnitId]: Yup.number().required('app.error.required'),
            [fieldNames.supplier]: Yup.object().nullable().required('app.error.required'),
            [fieldNames.globalTradeItemNumber]: Yup.string()
                .nullable()
                .min(12, 'app.error.gtinLength')
                .max(13, 'app.error.gtinLength'),
            [fieldNames.deliveryStatus]: Yup.string(),
            [fieldNames.deliveryTimeInDays]: Yup.number().when('deliveryStatus', {
                is: DeliveryStatus.Tracked,
                then: Yup.number().min(1, 'app.error.required').required('app.error.required'),
            }),
            [fieldNames.minimumOrderQuantity]: Yup.number()
                .required('app.error.required')
                .moreThan(0, 'app.error.greaterZero'),
            [fieldNames.maximumOrderQuantity]: Yup.number().nullable().moreThan(0, 'app.error.greaterZero'),
            [fieldNames.recommendedOrderQuantity]: Yup.number().nullable().moreThan(0, 'app.error.greaterZero'),
            [fieldNames.quantityInterval]: Yup.number()
                .required('app.error.required')
                .moreThan(0, 'app.error.greaterZero'),
            [fieldNames.packingUnits]: Yup.array()
                .test({
                    name: 'minimumQuantity',
                    test: yupUniqueTestFunction('minimumQuantity', 'app.error.uniqueMinimumQuantity'),
                })
                .test({
                    name: 'uniqueUnitId',
                    test: yupUniqueTestFunction('unitId', 'app.error.uniqueUnitId'),
                })
                .of(
                    Yup.object().shape(
                        {
                            unitId: Yup.number().when(['minimumQuantity', 'maximumQuantity'], {
                                is: (minimumQuantity: number, maximumQuantity: number) =>
                                    minimumQuantity || maximumQuantity,
                                then: Yup.number().typeError('app.error.required').required('app.error.required'),
                                otherwise: Yup.number().nullable(),
                            }),
                            minimumQuantity: Yup.number().when(['unitId', 'maximumQuantity'], {
                                is: (unitId: number, maximumQuantity: number) =>
                                    unitId || unitId === 0 || maximumQuantity,
                                then: Yup.number().required('app.error.required').moreThan(0, 'app.error.greaterZero'),
                                otherwise: Yup.number().nullable(),
                            }),
                            maximumQuantity: Yup.number()
                                .nullable()
                                .moreThan(0, 'app.error.greaterZero')
                                .min(Yup.ref('minimumQuantity'), 'app.error.greaterThanMinimum'),
                        },
                        [
                            ['minimumQuantity', 'maximumQuantity'],
                            ['unitId', 'minimumQuantity'],
                            ['unitId', 'maximumQuantity'],
                        ]
                    )
                ),
        },
        [[fieldNames.contentUnitId, fieldNames.contentQuantity]]
    );
